import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { useData } from '../context/DataContext';
import { Play, X, ZoomIn, Award } from 'lucide-react';
import Translate from '../components/Translate';

const getYoutubeThumbnail = (url) => {
  if (!url) return '';
  let videoId = '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === 11) {
    videoId = match[2];
  } else {
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length === 11) {
      videoId = lastPart;
    }
  }
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

const cleanTitle = (title, type) => {
  if (!title) return '';
  let cleaned = title;
  if (type === 'image') {
    cleaned = cleaned.replace(/factory\s+process\s*[-|:]?\s*/i, '');
  } else if (type === 'video') {
    cleaned = cleaned.replace(/corporate\s+broadcast\s*[-|:]?\s*/i, '');
  }
  return cleaned.trim();
};

const Gallery = () => {
  const [mainView, setMainView] = useState('gallery'); // 'gallery' or 'awards'
  const [filter, setFilter] = useState('image');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const { data } = useData();

  const categories = [
    { id: 'image', name: 'Photos' },
    { id: 'video', name: 'Videos' },
  ];

  const mediaItems = [
    ...(data.gallery?.images || []).map(img => ({ 
      ...img, 
      title: cleanTitle(img.title, 'image'),
      type: 'image', 
      category: 'image' 
    })),
    ...(data.gallery?.videos || []).map(vid => ({ 
      ...vid, 
      title: cleanTitle(vid.title, 'video'),
      type: 'video', 
      category: 'video', 
      videoUrl: vid.url, 
      url: getYoutubeThumbnail(vid.url) || 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=800&auto=format&fit=crop' 
    })),
  ];

  const filteredMedia = mediaItems.filter(item => item.category === filter);

  // Awards logic
  const awardsData = data.awards || [];
  const allAwards = Array.isArray(awardsData)
    ? awardsData
    : [
        ...(awardsData.national || []),
        ...(awardsData.state || []),
        ...(awardsData.safety || [])
      ];

  // Dynamic header titles
  const headerTitle = mainView === 'gallery' ? 'Factory Gallery' : 'Honors & Recognition';
  const headerSubtitle = mainView === 'gallery' 
    ? 'A Visual Journey of Excellence'
    : 'A testament to our unwavering commitment to quality, safety, and sustainable excellence in the sugarcane industry.';

  return (
    <div className="min-h-screen bg-ivory pb-24 font-body selection:bg-emerald selection:text-white">
      <PageHeader 
        title={<Translate>{headerTitle}</Translate>} 
        subtitle={<Translate>{headerSubtitle}</Translate>} 
      />

      {/* ── Main View Toggle Button ── */}
      <div className="pt-12 flex justify-center">
        <div className="flex bg-white p-1.5 rounded-2xl border border-gray-100 shadow-premium gap-1">
          <button
            onClick={() => setMainView('gallery')}
            className={`px-8 py-3 rounded-xl text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 ${
              mainView === 'gallery'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald hover:bg-ivory'
            }`}
          >
            <Translate>Visual Gallery</Translate>
          </button>
          <button
            onClick={() => setMainView('awards')}
            className={`px-8 py-3 rounded-xl text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 ${
              mainView === 'awards'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald hover:bg-ivory'
            }`}
          >
            <Translate>Honors & Awards</Translate>
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mainView === 'gallery' ? (
          <motion.div
            key="gallery-view"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35 }}
          >
            {/* Tab Switcher for Photos/Videos */}
            <section className="pt-8 pb-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-center">
                  <div className="flex bg-white/60 backdrop-blur-md p-1.5 rounded-xl border border-gray-100 shadow-premium gap-1">
                    {categories.map((cat) => {
                      const isActive = filter === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setFilter(cat.id)}
                          className={`px-6 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wider transition-all duration-300 ${
                            isActive 
                              ? 'bg-forest text-white' 
                              : 'text-slate hover:text-forest'
                          }`}
                        >
                          <Translate>{cat.name}</Translate>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Media Grid */}
            <section className="pb-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {filteredMedia.length === 0 ? (
                  <div className="py-16 text-center premium-card max-w-sm mx-auto">
                    <h4 className="text-md font-heading font-bold text-charcoal mb-1"><Translate>No Media Published</Translate></h4>
                    <p className="text-xs text-slate/50 px-6"><Translate>Check back later for updates.</Translate></p>
                  </div>
                ) : (
                  <motion.div 
                    layout
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  >
                    <AnimatePresence mode='popLayout'>
                      {filteredMedia.map((item) => (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.3 }}
                          className="group relative cursor-pointer flex flex-col justify-between"
                          onClick={() => setSelectedMedia(item)}
                        >
                          <div className="relative overflow-hidden rounded-2xl aspect-video bg-mist border border-gray-100 shadow-premium">
                            <img 
                              src={item.url} 
                              alt={item.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              loading="lazy"
                            />
                            
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-forest/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <motion.div 
                                whileHover={{ scale: 1.1 }}
                                className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center shadow-lg"
                              >
                                {item.type === 'video' ? (
                                  <Play className="text-emerald fill-emerald ml-0.5" size={20} />
                                ) : (
                                  <ZoomIn className="text-emerald" size={20} />
                                )}
                              </motion.div>
                            </div>

                            {item.type === 'video' && (
                              <div className="absolute top-3 right-3 bg-red-500 text-white text-[9px] font-heading font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg shadow-sm flex items-center gap-1">
                                <Play size={8} className="fill-white" />
                                <span><Translate>Video</Translate></span>
                              </div>
                            )}
                          </div>

                          <div className="mt-4 px-1 pb-2 text-center">
                            <h3 
                              className="text-sm font-heading font-bold text-charcoal group-hover:text-emerald transition-colors leading-snug line-clamp-2 px-2"
                              title={item.title}
                            >
                              <Translate>{item.title}</Translate>
                            </h3>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div
            key="awards-view"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35 }}
          >
            {/* Awards Content */}
            <section className="py-12 pb-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {allAwards.length === 0 ? (
                  <div className="py-16 text-center premium-card max-w-sm mx-auto">
                    <Award className="text-slate/20 mx-auto mb-4" size={40} />
                    <h4 className="text-md font-heading font-bold text-charcoal mb-1">
                      <Translate>No Awards Published</Translate>
                    </h4>
                    <p className="text-xs text-slate/50 px-6">
                      <Translate>Check back later for updates on our honors and achievements.</Translate>
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {allAwards.map((award, aIdx) => (
                      <motion.div
                        key={award.id || aIdx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: aIdx * 0.05 }}
                        className="group premium-card overflow-hidden flex flex-col justify-between"
                      >
                        <div>
                          <div className="relative aspect-[16/10] overflow-hidden bg-mist">
                            <img 
                              src={award.image || 'https://images.unsplash.com/photo-1578574515313-ad99999849c6?q=80&w=800&auto=format&fit=crop'} 
                              alt={award.title}
                              className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-105"
                              loading="lazy"
                            />
                            {award.year && (
                              <div className="absolute top-4 left-4">
                                <span className="px-3.5 py-1 bg-white/95 backdrop-blur-md rounded-lg text-gold text-[10px] font-heading font-extrabold shadow-sm uppercase tracking-wider border border-gold/10">
                                  {award.year}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-6">
                            <h3 className="text-lg font-heading font-bold text-charcoal mb-3 leading-snug group-hover:text-emerald transition-colors min-h-[3rem] line-clamp-2">
                              <Translate>{award.title}</Translate>
                            </h3>
                            <p className="text-slate text-xs leading-relaxed line-clamp-3">
                              <Translate>{award.desc || 'Recognized for excellence in technical efficiency and sustainable manufacturing practices.'}</Translate>
                            </p>
                          </div>
                        </div>
                        <div className="p-6 pt-4 mt-2 border-t border-gray-100 flex items-center justify-between bg-ivory/50">
                          <div className="flex flex-col">
                            <span className="text-[9px] text-slate/40 uppercase font-heading font-bold tracking-wider mb-0.5">
                              <Translate>Presented By</Translate>
                            </span>
                            <span className="text-xs font-heading font-bold text-charcoal line-clamp-1">
                              <Translate>{award.presenter}</Translate>
                            </span>
                          </div>
                          <div className="w-8 h-8 bg-white border border-gray-100 rounded-lg flex items-center justify-center shadow-sm group-hover:bg-gold group-hover:border-gold transition-all">
                            <Award className="text-slate/30 group-hover:text-white transition-colors" size={16} />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Trust Quote Section */}
            <section className="py-20 bg-white border-t border-gray-100">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <div className="text-gold/20 text-6xl font-serif leading-none mb-4">"</div>
                <p className="text-xl md:text-2xl font-heading font-bold text-charcoal leading-relaxed mb-6 italic -mt-6">
                  <Translate>Awards are not our final goal, but they motivate us to push the boundaries of what is possible in sustainable manufacturing.</Translate>
                </p>
                <div className="gold-divider mx-auto"></div>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox for Gallery images/videos */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-forest/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
            onClick={() => setSelectedMedia(null)}
          >
            <button 
              onClick={() => setSelectedMedia(null)}
              className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white transition-all shadow-md backdrop-blur-sm z-[110] cursor-pointer"
            >
              <X size={20} />
            </button>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl w-full flex flex-col items-center"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking media itself
            >
              <div className="w-full aspect-video rounded-2xl overflow-hidden bg-black shadow-premium-xl border border-white/5">
                {selectedMedia.type === 'video' ? (
                  <iframe 
                    src={selectedMedia.videoUrl} 
                    className="w-full h-full"
                    title={selectedMedia.title}
                    allowFullScreen
                  ></iframe>
                ) : (
                  <img 
                    src={selectedMedia.url} 
                    alt={selectedMedia.title}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <h3 className="text-white text-center text-base md:text-lg font-heading font-bold mt-4 tracking-wide px-4">
                <Translate>{selectedMedia.title}</Translate>
              </h3>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
